import { QuickSwitchUser } from 'api/models/Users';
import { getLastUsedOrgId } from 'hooks/useLastUsedOrgId';

const STORAGE_KEY = 'quick_switch_users_cache';

/** The client-encrypted refresh token and the parameters needed to decrypt it. */
export type PinTokenStore = { salt: string; iv: string; ciphertext: string };

/** Local copy of a user's pin.key. */
export type PinKeyCacheEntry = {
  userId: string;
  pinKeyPath: string;
  // absolute refresh token expiry (epoch ms), unencrypted so it can be checked before asking for a PIN
  expiryEpochMs: number;
  tokenStore: PinTokenStore;
  // re-wrapped locally and not yet written back to the keystore
  pendingUpload?: boolean;
  // PIN forgotten while logged out; the keystore object is deleted after the next login
  pendingDelete?: boolean;
};

export type CachedUser = QuickSwitchUser & {
  pin?: PinKeyCacheEntry;
  // end of the last PIN lockout seen on this browser (epoch ms)
  lockedUntilEpochMs?: number;
};
export type CachedOrg = { name: string; users: Record<string, CachedUser> };
// by Auth0 org id; users by lower-cased email, since the same email can exist in several orgs
export type UsersCache = Record<string, CachedOrg>;

export const normaliseEmail = (email: string) => email.trim().toLowerCase();

export const toUserMap = (users: QuickSwitchUser[], existing: Record<string, CachedUser> = {}) =>
  Object.fromEntries(
    users.map(user => {
      const key = normaliseEmail(user.email);
      return [key, { ...user, pin: existing[key]?.pin, lockedUntilEpochMs: existing[key]?.lockedUntilEpochMs }];
    }),
  );

// Emails are base64-encoded at rest so they aren't casually readable in browser storage. This is
// obfuscation, not protection; see pin-auth-services.md.
const encodeEmail = (email: string): string => {
  const bytes = new TextEncoder().encode(email);
  return btoa(Array.from(bytes, byte => String.fromCharCode(byte)).join(''));
};

// A plain email is never valid base64 ('@'), so entries stored before encoding pass through as-is.
const decodeEmail = (stored: string): string => {
  try {
    return new TextDecoder().decode(Uint8Array.from(atob(stored), char => char.charCodeAt(0)));
  } catch {
    return stored;
  }
};

const mapUserEmails = (users: Record<string, CachedUser>, convert: (email: string) => string) =>
  Object.fromEntries(
    Object.entries(users).map(([key, user]) => [convert(key), { ...user, email: convert(user.email) }]),
  );

export const readUsersCache = (): UsersCache => {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null');
    if (Array.isArray(parsed)) {
      // flat list from before the cache was org-scoped
      const orgId = getLastUsedOrgId();
      return orgId ? { [orgId]: { name: '', users: toUserMap(parsed) } } : {};
    }
    return Object.fromEntries(
      Object.entries((parsed ?? {}) as UsersCache).map(([orgId, org]) => [
        orgId,
        { ...org, users: mapUserEmails(org.users, decodeEmail) },
      ]),
    );
  } catch {
    return {};
  }
};

export const writeUsersCache = (cache: UsersCache): void => {
  const nonEmpty = Object.entries(cache)
    .filter(([, org]) => Object.keys(org.users).length)
    .map(([orgId, org]) => [orgId, { ...org, users: mapUserEmails(org.users, encodeEmail) }]);
  if (nonEmpty.length) localStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(nonEmpty)));
  else localStorage.removeItem(STORAGE_KEY);
};
